/* eslint-disable @typescript-eslint/no-explicit-any */
import { allowedOrigins } from "@/config/auth.config";
import { authStore } from "@/store/auth.store";
import {
  AuthFlowType,
  CognitoIdentityProviderClient,
  GlobalSignOutCommand,
  InitiateAuthCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { AuthTokens } from "./auth-tokens";

export const listenToSignInAction = () => {
  // Listen for the postMessage event to receive tokens
  window.addEventListener("message", (event) => {
    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    const { idToken, accessToken, refreshToken } = event.data;

    if (idToken && accessToken && refreshToken) {
      storeAuthTokens(idToken, accessToken, refreshToken);
      window.location.reload();
    } else {
      console.warn("No valid tokens received.");
    }
  });
};

/**
 * Refresh the authentication tokens using the refresh token.
 * Connects to the Cognito Identity Provider to refresh the tokens.
 */
export const refreshAuthTokens = async () => {
  const authTokens = getAuthTokens();

  if (!authTokens) {
    throw new Error("Auth tokens not found");
  }

  const client = new CognitoIdentityProviderClient({
    region: import.meta.env.VITE_AWS_REGION,
  });

  const params = {
    AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
    ClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: authTokens.RefreshToken,
    },
  };

  const command = new InitiateAuthCommand(params);
  const response = await client.send(command);

  if (response.AuthenticationResult) {
    storeAuthTokens(
      response.AuthenticationResult.IdToken || "",
      response.AuthenticationResult.AccessToken || "",
      authTokens.RefreshToken,
    );

    authStore.getState().actions.init();
  } else {
    console.warn("Failed to refresh auth tokens.");
  }
};

/**
 * Get the auth tokens from the local storage
 *
 * @returns AuthTokens
 */
export const getAuthTokens = () => {
  const authTokens = AuthTokens.fromStorage();

  return authTokens;
};

/**
 * Invalidate the authentication tokens using Cognito Identity Provider.
 */
const invalidateAuthTokens = async () => {
  const authTokens = getAuthTokens();

  if (!authTokens) {
    throw new Error("Auth tokens not found");
  }

  const client = new CognitoIdentityProviderClient({
    region: import.meta.env.VITE_AWS_REGION,
  });

  const command = new GlobalSignOutCommand({
    AccessToken: authTokens.AccessToken,
  });

  return client.send(command);
};

/**
 * Sets the authentication tokens in the local storage.
 *
 * @param tokens - An object containing the authentication tokens.
 */
export const storeAuthTokens = (
  idToken: string,
  accessToken: string,
  refreshToken: string,
) => {
  new AuthTokens(idToken, accessToken, refreshToken).store();
};

/**
 * Removes the authentication tokens from the local storage.
 */
export const removeAuthTokens = () => {
  AuthTokens.remove();
};

/**
 * Sign out the user by invalidating the authentication tokens and removing
 * them from the storage
 */
export const signOut = async () => {
  try {
    await invalidateAuthTokens();
  } catch (error: any) {
    if (error.name === "NotAuthorizedException") {
      console.warn("Not authorized. Ignoring...");
    } else {
      throw error;
    }
  }

  await removeAuthTokens();

  window.location.reload();
};

export const revalidateSession = async () => {
  try {
    await refreshAuthTokens();
  } catch (error: any) {
    if (error.name === "NotAuthorizedException") {
      await removeAuthTokens();

      window.location.reload();

      console.warn("Not authorized. Ignoring...");
    }
  }
};

/**
 * Check if the token is expired
 *
 * @returns boolean
 */
export const isIdTokenExpired = (): boolean => {
  const authTokens = getAuthTokens();

  if (!authTokens.IdToken) {
    return true;
  }

  return authTokens.isExpired();
};
